import { useShopInfoQuery, useUpdatePrivateMetadataMutation } from "@dashboard/graphql";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo } from "react";

import { WizardPersistedState } from "../types";

const STORAGE_KEY = "wsm_onboarding_wizard_state";
const SESSION_STORAGE_KEY = "wsm_onboarding_wizard_state_cache";

function isValidState(data: unknown): data is WizardPersistedState {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    Array.isArray(obj.completedSteps) &&
    Array.isArray(obj.skippedSteps) &&
    typeof obj.wizardCompleted === "boolean" &&
    typeof obj.startedAt === "string"
  );
}

type PrivateMetadataItem = {
  key: string;
  value: string;
};

export const readWizardState = (
  metadata: PrivateMetadataItem[] | null | undefined,
): WizardPersistedState | null => {
  const raw = metadata?.find(item => item.key === STORAGE_KEY)?.value;

  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!isValidState(parsed)) {
      console.warn("[WizardStorage] Invalid persisted state in shop private metadata, ignoring");

      return null;
    }

    return parsed;
  } catch {
    console.warn("[WizardStorage] Could not parse persisted state from shop private metadata");

    return null;
  }
};

const readWizardStateFromSession = (): WizardPersistedState | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!isValidState(parsed)) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);

      return null;
    }

    return parsed;
  } catch {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);

    return null;
  }
};

const writeWizardStateToSession = (state: WizardPersistedState) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
};

const clearWizardStateFromSession = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

export const useWizardPersistence = () => {
  const { data, loading } = useShopInfoQuery();
  const persistedState = useMemo(
    () => readWizardStateFromSession() ?? readWizardState(data?.shop?.privateMetadata),
    [data?.shop],
  );

  return {
    loading,
    persistedState,
    wizardCompleted: persistedState?.wizardCompleted ?? false,
  };
};

export const useWizardStorage = () => {
  const { data, loading } = useShopInfoQuery();
  const [updatePrivateMetadata] = useUpdatePrivateMetadataMutation();
  const persistedState = useMemo(
    () => readWizardStateFromSession() ?? readWizardState(data?.shop?.privateMetadata),
    [data?.shop],
  );

  const persistStateRemotely = useCallback(
    async (state: WizardPersistedState) => {
      if (!data?.shop?.id) {
        return;
      }

      try {
        await updatePrivateMetadata({
          variables: {
            id: data.shop.id,
            input: [{ key: STORAGE_KEY, value: JSON.stringify(state) }],
            keysToDelete: [],
          },
        });
      } catch {
        console.warn("[WizardStorage] Could not persist wizard state to shop private metadata");
      }
    },
    [data?.shop?.id, updatePrivateMetadata],
  );

  const saveStateImmediate = useCallback(
    async (state: WizardPersistedState) => {
      writeWizardStateToSession(state);
      await persistStateRemotely(state);
    },
    [persistStateRemotely],
  );

  const clearState = useCallback(async () => {
    clearWizardStateFromSession();

    if (!data?.shop?.id) {
      return;
    }

    try {
      await updatePrivateMetadata({
        variables: {
          id: data.shop.id,
          input: [],
          keysToDelete: [STORAGE_KEY],
        },
      });
    } catch {
      console.warn("[WizardStorage] Could not clear wizard state from shop private metadata");
    }
  }, [data?.shop?.id, updatePrivateMetadata]);

  const debouncedRemoteSaveState = useMemo(
    () => debounce(persistStateRemotely, 1000),
    [persistStateRemotely],
  );

  const saveState = useCallback(
    (state: WizardPersistedState) => {
      writeWizardStateToSession(state);
      debouncedRemoteSaveState(state);
    },
    [debouncedRemoteSaveState],
  );

  useEffect(() => () => debouncedRemoteSaveState.cancel(), [debouncedRemoteSaveState]);

  return {
    loading,
    persistedState,
    wizardCompleted: persistedState?.wizardCompleted ?? false,
    saveStateImmediate,
    saveState,
    cancelPendingSave: debouncedRemoteSaveState.cancel,
    clearState,
  };
};
